import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["avatarBackgroundColorToken", "avatarBackgroundColorTokenArgs", "avatarIcon", "children", "description", "disabled", "focused", "hasCheckbox", "hasUntruncatedDescription", "hasUntruncatedTitle", "hovered", "isChecked", "itemThumbnailBackgroundColor", "itemThumbnailResizeMode", "itemThumbnailUri", "keyboardFocused", "last", "logoUri", "noIcon", "onBlur", "onClick", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "renderBottom", "status", "statusType", "title", "titleSuffix", "UNSAFE_containerProps", "value", "valueSuccess"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { Fragment } from 'react';
import View from '@bubble-ui/primitive-view';
import { useDesignContext } from '@klarna/designtokens';
import Touchable from '@bubble-ui/primitive-touchable';
import Avatar from '@bubble-ui/molecule-avatar';
import Text from '@bubble-ui/primitive-text';
import InteractiveSelectorBackground from '@bubble-ui/atom-interactive-selector-background';
import SelectorBottomSeparator from '@bubble-ui/atom-selector-bottom-separator';
import TransactionCheckmark from '@bubble-ui/atom-transaction-checkmark';
import { platformSelect, isNative } from '@bubble-ui/util-platform';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { ItemThumbnail } from './ItemThumbnail';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { defaultDesignTokens } from './defaultDesignTokens';
var SEPARATOR = '-';
var Title = _ref => {
  var {
    hasUntruncatedTitle,
    paragraphSecondaryCorrectFootprintBlockMediumStyle,
    title,
    titleSuffix,
    titleSuffixStyle
  } = _ref;
  return /*#__PURE__*/React.createElement(Text, _extends({}, isNative ? {
    numberOfLines: !hasUntruncatedTitle ? 1 : 0
  } : {}, {
    tag: "paragraph",
    style: paragraphSecondaryCorrectFootprintBlockMediumStyle
  }), title, titleSuffix && /*#__PURE__*/React.createElement(Text, {
    style: titleSuffixStyle
  }, "\xA0", SEPARATOR, "\xA0", titleSuffix));
};
var Description = _ref2 => {
  var {
    description,
    descriptionStyle,
    hasUntruncatedDescription,
    paragraphTertiaryCorrectFootprintEllipsisStyle,
    status
  } = _ref2;
  return /*#__PURE__*/React.createElement(Text, _extends({}, isNative ? {
    numberOfLines: !hasUntruncatedDescription ? 1 : 0
  } : {}, {
    tag: "paragraph",
    style: paragraphTertiaryCorrectFootprintEllipsisStyle
  }), description != null && description !== '' && /*#__PURE__*/React.createElement(Text, {
    style: paragraphTertiaryCorrectFootprintEllipsisStyle
  }, description, status != null ? /*#__PURE__*/React.createElement(Text, {
    style: paragraphTertiaryCorrectFootprintEllipsisStyle
  }, "\xA0", SEPARATOR, "\xA0") : ''), status != null && /*#__PURE__*/React.createElement(Text, {
    style: descriptionStyle
  }, status));
};
var AvatarSpaceOffset = _ref3 => {
  var {
    designContext,
    itemThumbnailUri
  } = _ref3;
  return /*#__PURE__*/React.createElement(View, {
    style: {
      flexDirection: 'row'
    }
  }, /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      minHeight: 10,
      width: 50
    }
  }), itemThumbnailUri !== undefined && /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      minHeight: 10,
      width: 10
    }
  })), /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      minHeight: 10,
      width: 5
    }
  }));
};
var ChildrenBlock = _ref4 => {
  var {
    designContext,
    children
  } = _ref4;
  return /*#__PURE__*/React.createElement(View, null, /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 10
    }
  }), children);
};
var TouchableOrUntouchable = _ref5 => {
  var {
    children,
    disabled,
    onClick,
    onPress,
    style,
    UNSAFE_containerProps,
    wrapperProps
  } = _ref5;
  if (onClick || onPress) {
    return /*#__PURE__*/React.createElement(Touchable, _extends({}, UNSAFE_containerProps, {
      onClick: onClick,
      onPress: onPress,
      disabled: disabled,
      style: style,
      accessibilityState: {
        disabled
      }
    }, wrapperProps), children);
  }
  var accessibilityProps = platformSelect({
    web: {
      tabIndex: 0
    },
    native: {
      accessible: true,
      accessibilityRole: 'text'
    }
  });
  return /*#__PURE__*/React.createElement(View, _extends({}, UNSAFE_containerProps, accessibilityProps, wrapperProps), children);
};
var Transaction = _ref6 => {
  var {
      avatarBackgroundColorToken,
      avatarBackgroundColorTokenArgs,
      avatarIcon,
      children,
      description,
      disabled,
      focused,
      hasCheckbox,
      hasUntruncatedDescription,
      hasUntruncatedTitle,
      hovered,
      isChecked,
      itemThumbnailBackgroundColor,
      itemThumbnailResizeMode,
      itemThumbnailUri,
      keyboardFocused,
      last,
      logoUri,
      noIcon,
      onBlur,
      onClick,
      onFocus,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPress,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed,
      renderBottom,
      status,
      statusType,
      title,
      titleSuffix,
      UNSAFE_containerProps,
      value,
      valueSuccess = false
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref6,
    props = _objectWithoutProperties(_ref6, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'Transaction',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var designContext = useDesignContext(defaultDesignTokens);
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined(_objectSpread(_objectSpread({}, props), {}, {
    disabled,
    focused,
    hovered,
    keyboardFocused,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed
  }));
  var {
    avatarWrapperStyle,
    descriptionWithStatusStyle,
    headerInnerStyle,
    rootStyle,
    headerStyle,
    titleSuffixStyle,
    touchableStyle,
    contentWrapperStyle,
    titleAndValueWrapper,
    transactionCheckmarkWrapperStyle,
    transactionContentWrapper,
    titleWrapper,
    descriptionWrapper,
    valueWrapper,
    descriptionStyle,
    paragraphSecondaryCorrectFootprintMediumStyle,
    paragraphSecondaryCorrectFootprintBlockMediumStyle,
    paragraphTertiaryCorrectFootprintEllipsisStyle
  } = getDefaultStyleSheet({
    designContext,
    disabled,
    hasCheckbox,
    hasUntruncatedDescription,
    hasUntruncatedTitle,
    noIcon,
    itemThumbnailUri,
    itemThumbnailBackgroundColor,
    statusType,
    valueSuccess
  });
  var renderContent = renderBottom;
  return /*#__PURE__*/React.createElement(TouchableOrUntouchable, {
    onClick: onClick,
    onPress: onPress,
    disabled: interactiveStateCombined.disabled,
    style: touchableStyle,
    UNSAFE_containerProps: UNSAFE_containerProps,
    wrapperProps: _objectSpread(_objectSpread({}, props), eventHandlers)
  }, /*#__PURE__*/React.createElement(View, {
    style: rootStyle
  }, /*#__PURE__*/React.createElement(InteractiveSelectorBackground, {
    focused: interactiveStateCombined.keyboardFocused,
    pressed: interactiveStateCombined.pressed
  }), hasCheckbox && /*#__PURE__*/React.createElement(View, {
    style: transactionCheckmarkWrapperStyle
  }, /*#__PURE__*/React.createElement(TransactionCheckmark, {
    isChecked: isChecked,
    focused: interactiveStateCombined.keyboardFocused,
    hovered: interactiveStateCombined.hovered,
    disabled: interactiveStateCombined.disabled,
    pressed: interactiveStateCombined.pressed
  })), !noIcon && /*#__PURE__*/React.createElement(View, {
    style: avatarWrapperStyle
  }, itemThumbnailUri ? /*#__PURE__*/React.createElement(ItemThumbnail, {
    uri: itemThumbnailUri,
    imgResizeMode: itemThumbnailResizeMode
  }) : /*#__PURE__*/React.createElement(Avatar, {
    altText: "",
    fallbackCharacter: title === null || title === void 0 ? void 0 : title[0],
    source: logoUri,
    medium: true,
    icon: avatarIcon,
    backgroundColorToken: avatarBackgroundColorToken,
    backgroundColorTokenArgs: avatarBackgroundColorTokenArgs
  })), /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 15
    }
  }), /*#__PURE__*/React.createElement(View, {
    style: headerStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: headerInnerStyle
  }, !description && !status ? /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 15
    }
  })) : /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 5
    }
  }), /*#__PURE__*/React.createElement(View, {
    style: titleAndValueWrapper
  }, /*#__PURE__*/React.createElement(View, {
    style: titleWrapper
  }, /*#__PURE__*/React.createElement(Title, {
    title,
    titleSuffix,
    titleSuffixStyle,
    hasUntruncatedTitle,
    paragraphSecondaryCorrectFootprintBlockMediumStyle
  })), value != null && !hasCheckbox && /*#__PURE__*/React.createElement(View, {
    style: valueWrapper
  }, /*#__PURE__*/React.createElement(Text, _extends({}, isNative ? {
    numberOfLines: 0
  } : {}, {
    tag: "paragraph",
    style: paragraphSecondaryCorrectFootprintMediumStyle
  }), value))), (description || status) && /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 10
    }
  }), /*#__PURE__*/React.createElement(View, {
    style: descriptionWrapper
  }, /*#__PURE__*/React.createElement(Description, {
    descriptionWithStatusStyle,
    description,
    descriptionStyle,
    status,
    hasUntruncatedDescription,
    paragraphTertiaryCorrectFootprintEllipsisStyle
  }))))), children && /*#__PURE__*/React.createElement(ChildrenBlock, {
    designContext: designContext,
    children
  }), renderContent && /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 5
    }
  }), /*#__PURE__*/React.createElement(View, {
    style: contentWrapperStyle
  }, !children && /*#__PURE__*/React.createElement(AvatarSpaceOffset, {
    designContext: designContext,
    itemThumbnailUri: itemThumbnailUri
  }), /*#__PURE__*/React.createElement(View, {
    style: transactionContentWrapper
  }, Boolean(renderBottom) && renderBottom))), /*#__PURE__*/React.createElement(View, {
    style: {
      backgroundColor: designContext.get('colors/backgrounds/transparent').toString(),
      width: '100%',
      height: 15
    }
  }), last || /*#__PURE__*/React.createElement(SelectorBottomSeparator, {
    hasLogo: !noIcon && children === undefined,
    logoSize: noIcon ? undefined : children !== undefined ? undefined : itemThumbnailUri === undefined ? 'medium' : undefined
  })));
};
Transaction.displayName = 'Transaction';
export default withProvisionalOverrideFromContext(Transaction);
//# sourceMappingURL=index.js.map